import React, { useState, useEffect, useRef } from "react";
import { FaBuilding, FaUpload, FaCamera, FaCheck, FaExclamationCircle } from "react-icons/fa";
import { apiService } from "../../../components/Config/api.service";

const Company = () => {
    const [company, setCompany] = useState({
        id: "",
        name: "",
        address: "",
        other_name: "",
        nb_day_week: "",
        nb_hour_day: "",
        total_leave_days_per_month: "",
        cfce: true,
        work_accident: 1.0,
        leave_management_mode: "paid_leave_no_ic",
        logo: false,
    });
    const [companyLoading, setCompanyLoading] = useState(false);
    const [companyMsg, setCompanyMsg] = useState({ type: "", text: "" });
    const fileInputRef = useRef();

    useEffect(() => {
        setCompanyLoading(true);
        apiService.getElement("/company/info")
            .then((res) => setCompany(res.data))
            .catch(() => setCompanyMsg({ type: "error", text: "Erreur lors du chargement." }))
            .finally(() => setCompanyLoading(false));
    }, []);

    const handleCompanyChange = (e) => {
        setCompany({ ...company, [e.target.name]: e.target.value });
    };

    const handleLogoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append("file", file);
        setCompanyLoading(true);
        setCompanyMsg({ type: "", text: "" });
        try {
            await apiService.saveElement("/company/upload_logo", formData, true);
            const res = await apiService.getElement("/company/info");
            setCompany(res.data);
            setCompanyMsg({ type: "success", text: "Logo mis à jour !" });
        } catch {
            setCompanyMsg({ type: "error", text: "Erreur lors du changement de logo." });
        }
        setCompanyLoading(false);
    };

    const handleCompanySave = async (e) => {
        e.preventDefault();
        setCompanyLoading(true);
        setCompanyMsg({ type: "", text: "" });
        try {
            await apiService.saveElement("/company/update", company);
            setCompanyMsg({ type: "success", text: "Paramètres enregistrés !" });
        } catch {
            setCompanyMsg({ type: "error", text: "Erreur lors de la mise à jour." });
        }
        setCompanyLoading(false);
    };

    return (
        <form onSubmit={handleCompanySave} className="space-y-8">
            {/* Section Logo et Informations générales */}
            <div className="space-y-6">
                {/* <div className="flex items-center gap-3 mb-4">
                    <div className="bg-blue-100 rounded-full p-2">
                        <FaBuilding className="text-blue-600" size={16} />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800">Informations générales</h4>
                </div> */}

                {/* Logo Section */}
                <div className="flex flex-col md:flex-row items-start gap-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex flex-col items-center gap-4">
                        {company.logo ? (
                            <img
                                src={`data:image/png;base64,${company.logo}`}
                                alt="Logo"
                                className="w-24 h-24 rounded-lg object-cover border-2 border-gray-200 shadow-sm"
                            />
                        ) : (
                            <div className="w-24 h-24 rounded-lg bg-gray-200 flex items-center justify-center border-2 border-dashed border-gray-300">
                                <FaCamera className="text-gray-400" size={24} />
                            </div>
                        )}
                        <button
                            type="button"
                            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-sm"
                            onClick={() => fileInputRef.current.click()}
                            disabled={companyLoading}
                        >
                            <FaUpload size={14} />
                            {company.logo ? 'Changer le logo' : 'Ajouter un logo'}
                        </button>
                        <input
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleLogoChange}
                        />
                    </div>

                    <div className="flex-1 space-y-4">
                        <div>
                            <label htmlFor="company-name" className="block text-sm font-medium text-gray-700 mb-2">
                                Nom de la société *
                            </label>
                            <input
                                id="company-name"
                                name="name"
                                value={company.name}
                                onChange={handleCompanyChange}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-[--color-primary] transition-colors duration-200"
                                placeholder="Nom de votre société"
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="company-address" className="block text-sm font-medium text-gray-700 mb-2">
                                Adresse
                            </label>
                            <textarea
                                id="company-address"
                                name="address"
                                value={company.address || ""}
                                onChange={handleCompanyChange}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-[--color-primary] transition-colors duration-200"
                                placeholder="Adresse complète de votre société"
                                rows={3}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Section Paramètres de travail */}
            <div className="space-y-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="bg-green-100 rounded-full p-2">
                        <FaBuilding className="text-green-600" size={16} />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800">Paramètres de travail</h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Jours de travail par semaine
                        </label>
                        <input
                            name="nb_day_week"
                            value={company.nb_day_week || ""}
                            onChange={handleCompanyChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-[--color-primary] transition-colors duration-200"
                            type="number"
                            min="1"
                            max="7"
                            placeholder="Ex: 5"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Heures de travail par jour
                        </label>
                        <input
                            name="nb_hour_day"
                            value={company.nb_hour_day || ""}
                            onChange={handleCompanyChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-[--color-primary] transition-colors duration-200"
                            type="number"
                            min="1"
                            max="24"
                            placeholder="Ex: 8"
                        />
                    </div>
                </div>
            </div>

            {/* Section Gestion des congés */}
            <div className="space-y-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="bg-purple-100 rounded-full p-2">
                        <FaBuilding className="text-purple-600" size={16} />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800">Gestion des congés</h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Mode de gestion des congés
                        </label>
                        <select
                            name="leave_management_mode"
                            value={company.leave_management_mode || "paid_leave_no_ic"}
                            onChange={handleCompanyChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-[--color-primary] transition-colors duration-200"
                        >
                            <option value="paid_leave_no_ic">
                                Absence payée sans indemnité (IC versée au départ)
                            </option>
                            <option value="unpaid_leave_with_ic">
                                Absence non payée avec indemnité
                            </option>
                            <option value="paid_leave_with_ic">
                                Absence payée avec indemnité
                            </option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Jours de congé par mois
                        </label>
                        <input
                            name="total_leave_days_per_month"
                            value={company.total_leave_days_per_month || ""}
                            onChange={handleCompanyChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-[--color-primary] transition-colors duration-200"
                            type="number"
                            min="0"
                            step="0.1"
                            placeholder="Ex: 2.5"
                        />
                    </div>
                </div>
            </div>

            {/* Section Cotisations */}
            <div className="space-y-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="bg-orange-100 rounded-full p-2">
                        <FaBuilding className="text-orange-600" size={16} />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800">Cotisations et charges</h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Taux accident de travail (%)
                        </label>
                        <input
                            name="work_accident"
                            value={company.work_accident || 1}
                            onChange={handleCompanyChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-[--color-primary] transition-colors duration-200"
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="1.00"
                        />
                    </div>
                    <div className="flex items-center h-full">
                        <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200 w-full">
                            <input
                                name="cfce"
                                type="checkbox"
                                checked={company.cfce}
                                onChange={(e) => setCompany({ ...company, cfce: e.target.checked })}
                                className="w-4 h-4 text-[--color-primary] border-gray-300 rounded focus:ring-[--color-primary]"
                                id="cfce"
                            />
                            <label htmlFor="cfce" className="text-sm font-medium text-gray-700">
                                Contribution Forfaitaire à la Charge de l'Employeur (CFCE)
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            {/* Message de statut */}
            {companyMsg.text && (
                <div className={`flex items-center gap-3 p-4 rounded-lg border ${
                    companyMsg.type === "success" 
                        ? "bg-green-50 border-green-200 text-green-800" 
                        : "bg-red-50 border-red-200 text-red-800"
                }`}>
                    {companyMsg.type === "success" ? (
                        <FaCheck className="text-green-600" size={16} />
                    ) : (
                        <FaExclamationCircle className="text-red-600" size={16} />
                    )}
                    <span className="font-medium">{companyMsg.text}</span>
                </div>
            )}

            {/* Bouton de sauvegarde */}
            <div className="flex justify-end pt-6 border-t border-gray-200">
                <button
                    type="submit"
                    className="flex items-center gap-2 px-6 py-3 bg-[--color-primary] text-white rounded-lg hover:bg-[--color-primary] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    disabled={companyLoading}
                >
                    {companyLoading ? (
                        <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Enregistrement...
                        </>
                    ) : (
                        <>
                            <FaCheck size={16} />
                            Enregistrer les modifications
                        </>
                    )}
                </button>
            </div>
        </form>
    );
};

export default Company;